// policy.service.ts - Telefon ile arama fonksiyonu eklenmiş versiyon

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface Policy {
  saleChannel: string;
  orderNumber: string;
  packageId: string;
  orderDate: Date;
  policyStatus: string;
  customerName: string;
  customerPhone: string;
  customerIdentity: string;
  productName: string;
  deviceSerial: string;
  deviceBrand: string;
  company: string;
  policyNumber: string;
  policyStartDate?: Date;
  policyEndDate?: Date;
  policyCancelledDate?: Date;
  price?: number;
  insuranceProductName?: string;
  insuranceBranch?: string;
  category?: string;
}

export interface CallRecord {
  id?: number;
  token: string;
  cccahnnel: string; // Backend'deki typo
  user: string;
  callid: string;
  phone: string;
  status: string;
  creationDate: string;
  updateDate: string;
}

@Injectable({
  providedIn: 'root'
})
export class PolicyService {

  private baseUrl = environment._baseUrl;
  private policyBasePath = `${this.baseUrl}/api/policy-documents`;
  
  // Call Center endpoint'leri
  // TEST için:
  private callCenterPath = `https://testapi.techpolabs.net/api/v1/callcenter`;
  
  // PRODUCTION için:
  // private callCenterPath = `${this.baseUrl}/api/v1/callcenter`;
  
  // Poliçe sorgulama endpoint'i (backend'de oluşturulmalı)
  private policySearchPath = `${this.baseUrl}/api/policies`;

  constructor(private http: HttpClient) {}

  /**
   * Call kaydı oluşturur ve token döndürür
   * @param ccchannel Call kanalı (Backend'de cccahnnel olarak yazılmış)
   * @param user Kullanıcı
   * @param callid Call ID
   * @param phone Telefon numarası
   * @returns Token ve success bilgisi
   */
  createCallRecord(ccchannel: string, user: string, callid: string, phone: string): Observable<any> {
    const params = new HttpParams()
      .set('cccahnnel', ccchannel) // Backend'deki typo'ya dikkat!
      .set('user', user)
      .set('callid', callid)
      .set('phone', phone);

    return this.http.post<any>(`${this.callCenterPath}/create`, null, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Token ve user bilgisi ile doğrulama yapar
   * @param token Doğrulanacak token
   * @param user Kullanıcı bilgisi
   * @returns Doğrulama sonucu
   */
  validateTokenAndUser(token: string, user: string): Observable<any> {
    const params = new HttpParams()
      .set('token', token)
      .set('user', user);

    return this.http.get<any>(`${this.callCenterPath}/validate`, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Token ile call kaydını getirir
   * @param token Kayıt token'ı
   * @returns Call kaydı
   */
  getCallRecordByToken(token: string): Observable<any> {
    return this.http.get<any>(`${this.callCenterPath}/record/${token}`)
      .pipe(
        catchError(this.handleError)
      );
  }
/**
   * Telefon numarasına göre poliçeleri ara
   * @param phone Müşteri telefon numarası
   * @returns Poliçe listesi
   */
  searchPoliciesByPhone(phone: string): Observable<Policy[]> {
    const params = new HttpParams()
      .set('phone', phone);

    // Gerçek API bağlantısı:
    return this.http.get<any[]>(`${this.callCenterPath}/policies/search`, { params })
      .pipe(
        map(response => this.transformPolicyData(response)),
        catchError(this.handleError)
      );
  }

  /**
   * Transform API response to ensure proper data types
   * @param data - Raw policy data from API
   * @returns Transformed policy data
   */
  private transformPolicyData(data: any[]): Policy[] {
    if (!data || !Array.isArray(data)) {
      return [];
    }

    // Backend'den gelen TechpoProtocol veya Policy nesnelerini map ediyoruz
    return data.map(item => ({
      saleChannel: item.saleChannel || 'N/A',
      orderNumber: item.orderNumber || item.protocol.protocolNumber || '-',
      packageId: item.packageId || '-',
      orderDate: item.protocol.orderDate || item.creationDate ? new Date(item.orderDate || item.creationDate) : new Date(),
      policyStatus: item.protocol.policyStatus || 'ACTIVE',
      customerName: item.protocol.customerName || '-',
      customerPhone: item.protocol.customerPhone || '',
      customerIdentity: item.protocol.customerIdentity || '-',
      productName: item.protocol.productName || '-',
      deviceSerial: item.protocol.deviceSerial || '-',
      deviceBrand: item.protocol.deviceBrand || '-',
      company: item.insuranceCompany || '-',
      policyNumber: item.protocol.policyNumber || item.protocol.protocolNumber || '-',
      policyStartDate: item.protocol.policyStartDate ? new Date(item.protocol.policyStartDate) : null,
      policyEndDate: item.protocol.policyEndDate ? new Date(item.protocol.policyEndDate) : null,
      price: item.protocol.price ? parseFloat(item.protocol.price) : 0
    }));
  }

  /**
   * Poliçe seçimini kaydet
   * @param selectionData Seçim bilgileri
   * @returns İşlem sonucu
   */
  savePolicySelection(selectionData: any): Observable<any> {
    // TODO: Backend'de bu endpoint oluşturulmalı
    return this.http.post<any>(`${this.callCenterPath}/selection`, selectionData)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Handle HTTP errors
   * @param error - The error response
   * @returns Observable throwing an error
   */
  private handleError(error: any): Observable<never> {
    let errorMessage = 'Bir hata oluştu';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Hata: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = `Sunucu hatası: ${error.status}\nMesaj: ${error.message}`;
      
      // Handle specific error codes
      switch (error.status) {
        case 401:
          errorMessage = 'Yetkilendirme hatası. Token veya kullanıcı bilgilerini kontrol edin.';
          break;
        case 403:
          errorMessage = 'Bu işlem için yetkiniz bulunmamaktadır.';
          break;
        case 404:
          errorMessage = 'İstenen kaynak bulunamadı.';
          break;
        case 500:
          errorMessage = 'Sunucu hatası oluştu. Lütfen daha sonra tekrar deneyin.';
          break;
      }
    }

    console.error(errorMessage);
    return throwError(() => new Error(errorMessage));
  }

  // Mevcut diğer metodlar...
  sendPolicyEmail(mail: string, policyRef: string): Observable<any> {
    const params = new HttpParams()
      .set('mail', mail)
      .set('policyRef', policyRef);
    return this.http.post<any>(`${this.policyBasePath}/email`, null, { params: params });
  }

  sendPolicySms(policyRef: string, identityNumber: string, birthDate: string): Observable<any> {
    const params = new HttpParams()
      .set('policyRef', policyRef)
      .set('identityNumber', identityNumber)
      .set('birthDate', birthDate);
    return this.http.post<any>(`${this.policyBasePath}/sms`, null, { params: params });
  }
}